import { Row } from '~components/table-view';

import { CompanyShift, ShiftWithTimes } from '~lookups/models';

import { CompanyShiftError, CompanyShiftInput } from '~support-at-home/actions/companyShifts';

/**
 * `lookup_shifts.allow_weekdays` is Monday-indexed (Mon = 0). Labelled from a fixed list rather
 * than from dayjs, whose week start is a mutable global locale setting in this app.
 */
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * A band running 00:00 to 00:00 is the platform's full-day sentinel: pricing treats it as matching
 * every time of day rather than as a zero-length range. It is what makes the Saturday, Sunday and
 * public holiday bands cover their whole day, and it must never appear on a weekday band.
 */
export const FULL_DAY_TIME = '00:00';

const BAND_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export interface TimeBandRow extends Row {
  id: string;
  tag: string;
  name: string;
  days: string;
  /** The times this company is actually priced on - its own where set, the platform's where not. */
  start_time: string;
  end_time: string;
  default_start_time: string;
  default_end_time: string;
  is_editable: boolean;
  is_overridden: boolean;
  /** Reason this band was refused, in the words it was refused in. Empty when there is none. */
  issue: string;
}

/** Trims a `HH:mm:ss` time column down to the `HH:mm` an input and the API both work in. */
export const toBandTime = (value?: string | null): string => {
  if (!value) return '';
  const trimmed = String(value).trim().slice(0, 5);
  return BAND_TIME_PATTERN.test(trimmed) ? trimmed : '';
};

export const isBandTime = (value?: string | null): boolean => BAND_TIME_PATTERN.test(String(value ?? ''));

export const isFullDay = (startTime?: string | null, endTime?: string | null): boolean =>
  toBandTime(startTime) === FULL_DAY_TIME && toBandTime(endTime) === FULL_DAY_TIME;

/** "Mon - Fri", "Sat", "Any day" - contiguous runs collapse, everything else is listed. */
export const formatDays = (allowWeekdays: number[] = []): string => {
  const days = [...new Set(allowWeekdays)]
    .filter((day) => day >= 0 && day < WEEKDAY_LABELS.length)
    .sort((a, b) => a - b);
  if (days.length === 0) return '';
  if (days.length === WEEKDAY_LABELS.length) return 'Any day';

  const isContiguous = days.every((day, index) => index === 0 || day === days[index - 1] + 1);
  if (isContiguous && days.length > 2) return `${WEEKDAY_LABELS[days[0]]} - ${WEEKDAY_LABELS[days[days.length - 1]]}`;

  return days.map((day) => WEEKDAY_LABELS[day]).join(', ');
};

/**
 * Joins the platform bands to this company's overrides and reports the effective times - the
 * company's where it has set them, the platform's where it has not. Mirrors the merge pricing
 * itself does when it resolves a band at claim-build time.
 *
 * A band is editable when its *platform* times are not the full-day sentinel: a sentinel band
 * applies to its whole day, so giving it a range would change what the band means rather than
 * when it applies.
 */
export const buildTimeBandRows = (
  shifts: readonly ShiftWithTimes[],
  companyShifts: readonly CompanyShift[],
): TimeBandRow[] => {
  const overridesByShiftId = new Map(companyShifts.map((override) => [override.shift_id, override]));

  return [...shifts]
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map((shift) => {
      const override = overridesByShiftId.get(shift.id);
      const defaultStartTime = toBandTime(shift.start_time);
      const defaultEndTime = toBandTime(shift.end_time);

      return {
        id: shift.id,
        tag: shift.tag,
        name: shift.name,
        days: formatDays(shift.allow_weekdays),
        start_time: override ? toBandTime(override.start_time) : defaultStartTime,
        end_time: override ? toBandTime(override.end_time) : defaultEndTime,
        default_start_time: defaultStartTime,
        default_end_time: defaultEndTime,
        is_editable: !isFullDay(defaultStartTime, defaultEndTime),
        is_overridden: Boolean(override),
        issue: '',
      };
    });
};

/**
 * A band that applies for its whole day on the platform, but which this company holds real times
 * for. Such a band is locked - giving a whole-day band a range would change what it means rather
 * than when it applies - so the times can only have come from a write that predates this tab. Claim
 * pricing still resolves the band from the company's own row, so those times are what the work is
 * priced on, and reporting the platform's "All day" over the top of them would hide it.
 */
export const isLockedOverride = (row: TimeBandRow): boolean =>
  !row.is_editable && row.is_overridden && !isFullDay(row.start_time, row.end_time);

/** True when the row's times differ from the platform band's own times. */
export const isChangedFromDefault = (row: TimeBandRow): boolean =>
  row.start_time !== row.default_start_time || row.end_time !== row.default_end_time;

/**
 * The band faults that can be settled without asking the API. Whether the three bands still tile a
 * weekday is left to the API, which is the only authority on it, and is reported back per band.
 */
export const findLocalIssues = (rows: TimeBandRow[]): Record<string, string> => {
  const issues: Record<string, string> = {};

  rows
    .filter((row) => row.is_editable)
    .forEach(({ id, name, start_time, end_time }) => {
      if (!isBandTime(start_time) || !isBandTime(end_time)) {
        issues[id] = `${name} needs both a start time and an end time`;
        return;
      }
      if (isFullDay(start_time, end_time)) {
        issues[id] = `${name} cannot start and end at midnight - that would make it an all-day band`;
        return;
      }
      if (start_time === end_time) {
        issues[id] = `${name} starts and ends at the same time, so it covers no time at all`;
      }
    });

  return issues;
};

/** The editable bands, as the API's bulk save takes them. */
export const toSavePayload = (rows: TimeBandRow[]): CompanyShiftInput[] =>
  rows.filter((row) => row.is_editable).map(({ id, start_time, end_time }) => ({ shift_id: id, start_time, end_time }));

/**
 * Keys the API's rejection reasons to the rows they name, preferring `shift_id` and falling back to
 * `tag`. Anything that names no band the caller shows on its own, so no reason is ever swallowed.
 */
export const mapErrorsToRows = (
  rows: TimeBandRow[],
  errors: CompanyShiftError[] = [],
): { issues: Record<string, string>; unmatched: string[] } => {
  const issues: Record<string, string> = {};
  const unmatched: string[] = [];

  errors.forEach(({ shift_id, tag, message }) => {
    const row =
      rows.find(({ id }) => shift_id && id === shift_id) ?? rows.find(({ tag: rowTag }) => tag && rowTag === tag);
    if (!row) {
      unmatched.push(message);
      return;
    }
    issues[row.id] = issues[row.id] ? `${issues[row.id]} ${message}` : message;
  });

  return { issues, unmatched };
};
